import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch (e) {
      console.error("Auth refresh failed", e);
      setToken(null);
      setUser(null);
    } finally { setLoading(false); }
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleCallback = useCallback(async () => {
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) return false;
    const session_id = decodeURIComponent(m[1]);
    try {
      const { data } = await api.post("/auth/google/session", { session_id });
      setToken(data.token);
      setUser(data.user);
      // strip the session_id from the URL and land on the dashboard
      window.history.replaceState({}, document.title, window.location.pathname);
      if (window.location.pathname !== "/dashboard") window.location.assign("/dashboard");
    } catch (e) {
      console.error("Google auth failed", e);
      setUser(null);
    } finally { setLoading(false); }
    return true;
  }, []);

  useEffect(() => {
    // If returning from Google OAuth, exchange the session_id FIRST.
    if (window.location.hash?.includes("session_id=")) {
      handleGoogleCallback();
      return;
    }
    refresh();
  }, [refresh, handleGoogleCallback]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Persist token for the bearer header (works cross-origin); cookie is also set.
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };
  const signup = async (email, password, name, ref) => {
    const payload = { email, password, name };
    if (ref) payload.ref = ref;
    const { data } = await api.post("/auth/signup", payload);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  };
  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) { console.error("Logout failed", e); }
    setToken(null);
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, signup, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
